import { useId, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, KeyRound, Send, X } from 'lucide-react';

import { useLivelloIndietro } from '@/hooks/useLivelloIndietro';

interface SceltaProps {
  Icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  titolo: string;
  sottotitolo: string;
  testId: string;
  onClick: () => void;
}

function Scelta({ Icon, titolo, sottotitolo, testId, onClick }: SceltaProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="card-edge flex min-h-[64px] w-full items-center gap-3 rounded-2xl bg-bg px-3 py-2 text-left transition-opacity active:opacity-80"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand/15 text-brand">
        <Icon aria-hidden className="h-5 w-5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[15px] font-bold text-fg">{titolo}</span>
        <span className="text-xs text-fg-muted">{sottotitolo}</span>
      </span>
      <ChevronRight aria-hidden className="h-[18px] w-[18px] shrink-0 text-fg-muted" />
    </button>
  );
}

interface CodiceInvitoSheetProps {
  /** «Crea un codice»: chi chiama apre `InviteSheet` (il flusso di sempre). */
  onCrea: () => void;
  /** «Ho un codice»: chi chiama apre `AcceptInviteSheet` (il flusso di sempre). */
  onHoCodice: () => void;
  onClose: () => void;
}

/**
 * «Codice invito» nella scheda «Ranked» (reference «Anello che respira», L-2606, 30/09, tavola
 * `Foglio-codice.dc.html`): i due modi, «Crea un codice» e «Ho un codice».
 *
 * 🔑 **Un foglio che SCEGLIE, non il foglio segmentato della tavola** — scelta del cantiere, dentro
 * la delega del contratto (`ranked-bg` §5). La tavola mette i due modi in un foglio solo con un
 * selettore; farlo davvero vorrebbe dire spezzare `InviteSheet` e `AcceptInviteSheet` in contenuto +
 * guscio, e quei due fogli:
 *  - portano nel GUSCIO una regola che non e' estetica: in `InviteSheet` il fondo, la X e il tasto
 *    indietro ANNULLANO l'invito pendente (`close` = `cancelInvite` + `onClose`), e la garanzia sta in
 *    `tests/indietro-overlay-adozioni.test.tsx`;
 *  - servono anche la scheda «Gioca», che in questo giro non si tocca;
 *  - e un selettore dentro un foglio vorrebbe una regola che oggi non esiste: passare a «Ho un codice»
 *    con un invito pendente lo annulla, o lo lascia vivo senza che si veda?
 * ⇒ Qui si sceglie, e i due fogli di sempre si aprono INTATTI (stesse props di `BackgammonLobby`).
 * Il riuso e' il codice, non una copia.
 */
export function CodiceInvitoSheet({ onCrea, onHoCodice, onClose }: CodiceInvitoSheetProps) {
  const { t } = useTranslation('backgammon');
  const titoloId = useId();
  useLivelloIndietro(true, onClose);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titoloId}
      data-testid="codice-sheet"
    >
      <button
        type="button"
        aria-label={t('invite.close')}
        onClick={onClose}
        className="absolute inset-0 bg-black/60"
      />
      <div className="relative w-full max-w-md rounded-t-3xl bg-bg-secondary px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2.5">
        <div aria-hidden className="mx-auto mb-3 h-1 w-10 rounded-full bg-fg-muted/40" />
        <div className="flex items-start justify-between gap-2">
          <h2
            id={titoloId}
            className="font-display text-[17px] font-extrabold tracking-tight text-fg"
          >
            {t('ranked.code.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('invite.close')}
            className="-mr-2 -mt-2 grid h-11 w-11 shrink-0 place-items-center text-fg-muted active:opacity-80"
          >
            <X aria-hidden className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-3 flex flex-col gap-2">
          <Scelta
            Icon={Send}
            testId="codice-crea"
            titolo={t('ranked.code.create')}
            sottotitolo={t('ranked.code.createSub')}
            onClick={onCrea}
          />
          <Scelta
            Icon={KeyRound}
            testId="codice-ho"
            titolo={t('ranked.code.have')}
            sottotitolo={t('ranked.code.haveSub')}
            onClick={onHoCodice}
          />
        </div>
      </div>
    </div>
  );
}
